import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { downloadBlob } from "../../lib/downloadBlob";

/**
 * In a cohort paid by an institution (07.10.2026): who enrolled through the
 * link, confirmed or not, the team's "Confirmă" — which pays the place and
 * starts the dossier and the contract — and the list for the invoice, which
 * is made by hand.
 */
const lei = (n) => `${Number(n).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} lei`;

export default function InstitutionPanel({ groupId, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/admin/groups/${groupId}/institution`)).data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut încărca participanții instituției."));
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, call, ok) => {
    setBusy(key);
    try {
      const r = await call();
      toast.success(typeof ok === "function" ? ok(r.data) : ok);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut confirma."));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div className="py-4 text-sm text-[#6B7280]"><Loader2 size={14} className="mr-1 inline animate-spin" /> Se încarcă…</div>;
  const name = data.institution_name || "instituție";
  return (
    <section className="rounded-2xl border border-[#519872]/30 bg-[#519872]/[0.05] p-4" data-testid="institution-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-[#3c7457]">Plătește instituția · {name}</h3>
          <p className="mt-1 text-sm text-[#373A45]">
            {data.confirmed} {data.confirmed === 1 ? "participant confirmat" : "participanți confirmați"}
            {data.to_confirm > 0 && <> · <strong>{data.to_confirm} de confirmat</strong></>}
            {data.total > 0 && (
              <span className="block text-xs text-[#6B7280]" data-testid="institution-total">
                De facturat instituției până acum: <strong className="text-[#373A45] tabular-nums">{lei(data.total)}</strong>
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.to_confirm > 0 && (
            <button type="button" disabled={Boolean(busy)} data-testid="institution-confirm-all"
              onClick={() => run("all", () => api.post(`/admin/groups/${groupId}/institution-confirm`),
                (d) => `Am confirmat ${d.confirmed} ${d.confirmed === 1 ? "participant" : "participanți"}.`)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#3c7457] px-4 py-2 text-xs font-semibold text-white hover:bg-[#2f6045] disabled:opacity-60">
              {busy === "all" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Confirmă toți ({data.to_confirm})
            </button>
          )}
          <button type="button" disabled={!data.confirmed}
            onClick={() => downloadBlob(`/admin/groups/${groupId}/institution.xlsx`, `Participanti_${name}.xlsx`)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-semibold text-[#373A45] hover:border-[#3c7457] disabled:opacity-50">
            <Download size={13} /> Lista pentru factură (Excel)
          </button>
        </div>
      </div>
      <p className="mt-2 text-xs text-[#6B7280]">
        Confirmă doar profesorii instituției. La confirmare, locul e plătit în evidența noastră, iar participantul
        primește singur emailul pentru dosar și apoi contractul. Factura către instituție se face manual.
      </p>
      {data.rows.length > 0 && (
        <ul className="mt-3 divide-y divide-[#E5E7EB] rounded-xl border border-[#E5E7EB] bg-white">
          {data.rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
              <span className="min-w-0 flex-1">
                <span className="font-semibold text-[#373A45]">{r.nume} {r.prenume}</span>
                <span className="block text-xs text-[#6B7280]">{r.email}{r.unitate ? ` · ${r.unitate}` : ""}</span>
              </span>
              {r.amount > 0 && <span className="text-xs tabular-nums text-[#6B7280]">{lei(r.amount)}</span>}
              {r.confirmed ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#3c7457]">
                  <CheckCircle2 size={13} /> confirmat
                </span>
              ) : (
                <button type="button" disabled={Boolean(busy)} data-testid={`institution-confirm-${r.id}`}
                  onClick={() => run(r.id, () => api.post(`/admin/enrollments/${r.id}/institution-confirm`),
                    `${r.prenume} ${r.nume} e confirmat(ă).`)}
                  className="inline-flex items-center gap-1 rounded-full border border-[#3c7457]/40 px-3 py-1 text-xs font-semibold text-[#3c7457] hover:bg-[#3c7457]/10 disabled:opacity-60">
                  {busy === r.id && <Loader2 size={12} className="animate-spin" />} Confirmă
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
